import React, {
  FunctionComponent,
  useState,
  ChangeEvent,
  useEffect,
  KeyboardEvent,
} from "react";

import template from "lodash.template";

import "./index.scss";
import { WizardProps, Invocations } from "../../containers/Wizard";
import ButtonOptions from "../ButtonOptions";
import OrigamiPreview from "../OrigamiPreview";
import { Tab } from "@own-local/ad-unit/lib/AdUnit";
import { noNormalizer } from "../../shared/normalizers";
import { isUrlValid } from "../../shared/validations"

export const ORIGAMI_DATA_SPELLS = [
  "business",
  "business_name",
  "caption",
  "background",
  "logo",
  "layout",
  "address",
  "city",
  "zip",
  "business_phone",
  "website",
  "facebook",
  "twitter",
  "instagram",
  "additional_text",
  "email",
  "state",
];

const ORIGAMI_DATA_PREPENDS_MAP: OrigamiDataType = {
  facebook: "https://facebook.com/",
  twitter: "https://twitter.com/",
  instagram: "https://instagram.com/",
};

export type BusinessDataType = {
  uuid: string;
  name: string;
};

export type OrigamiDataType = {
  [key: string]: string | object | undefined;
};

type FillOrigamiStepProps = {
  previous?: string;
  next: string;
  question: string;
  target?: string;
  placeholder?: string;
  type?: string;
  required?: boolean;
  validation?: Function;
  spellName: string;
  wizard: WizardProps;
  description?: string;
  textArea?: boolean;
  prependValue?: string;
  selectedTab?: Tab;
  normalizer?: Function;
  showExpanded?: boolean;
};

const defineOrigamiData = (invocations: Invocations): OrigamiDataType => {
  return ORIGAMI_DATA_SPELLS.reduce<OrigamiDataType>(
    (origamiData, spell): OrigamiDataType => {
      const prependValue = ORIGAMI_DATA_PREPENDS_MAP[spell] || "";
      if (invocations[spell]) origamiData[spell] = `${prependValue}${invocations[spell]}`;

      if (spell === "logo") {
        origamiData["logo"] = isUrlValid(invocations["logo"]?.toString()) ? invocations["logo"] : ""
      }

      return origamiData;
    },
    {},
  );
};

export const FillOrigamiStep: FunctionComponent<FillOrigamiStepProps> = ({
  previous,
  next,
  question,
  target,
  placeholder = "Type your answer here...",
  type = "text",
  required = true,
  validation,
  spellName,
  wizard,
  description,
  textArea,
  prependValue,
  selectedTab,
  normalizer = noNormalizer,
  showExpanded,
}: FillOrigamiStepProps) => {
  const [origamiData, setOrigamiData] = useState<OrigamiDataType>(
    defineOrigamiData(wizard.invocations),
  );
  const [input, setInput] = useState<HTMLInputElement | HTMLTextAreaElement | null>();
  const initialValue = normalizer(String(wizard.invocations[spellName] || ""));
  const [value, setValue] = useState(initialValue);

  useEffect(() => {
    setValue(initialValue);
  }, [initialValue, setValue]);

  useEffect(() => {
    setOrigamiData(defineOrigamiData(wizard.invocations));
  }, [setOrigamiData, wizard.invocations]);

  const fetchPrependValue = (value: string) => {
    if (value === "" || !value) return undefined;
    return prependValue ? `${prependValue}${value}` : value;
  };

  const onChange = (val: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const value = normalizer(val.target.value);
    setValue(value);
    origamiData[spellName] = fetchPrependValue(value);
  };

  const handleSubmit = () => {
    if (!input?.reportValidity()) return;

    const invalid = validation ? validation(value) : null;

    if (invalid) console.log(invalid);
    else if (initialValue === value) wizard.changePath(next);
    else {
      if (spellName === 'business_phone' && (wizard?.publisher.country === 'US' || wizard?.publisher.country === 'CA')) {
        wizard.invocate(spellName, `+1${value}`, next);
      } else if (spellName === 'website'){
        const website = value.startsWith('http') ? value : `https://${value}`;
        wizard.invocate(spellName, website, next);
      } else {
        wizard.invocate(spellName, value, next);
      }
    }
  };

  const onKeyPress = (event: KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    if (event.key === "Enter") handleSubmit();
  };

  const renderQuestion = () => {
    const temp = template(question);
    const targetHtml = target ? `<strong>${target}</strong>` : "";
    const innerHtml = `${temp({ target: targetHtml })}?`;

    return {
      __html: innerHtml,
    };
  };

  const inputProps = {
    ref: (el: HTMLInputElement | HTMLTextAreaElement | null) => setInput(el),
    type: type,
    placeholder: placeholder,
    value: value,
    onChange: onChange,
    onKeyPress: onKeyPress,
    required: required,
    autoFocus: true,
  };

  const renderInput = (textArea?: boolean) => {
    if (textArea) return <textarea {...inputProps} />;
    return <input {...inputProps} />;
  };

  const defineTab = () => {
    if (selectedTab && !!value) return selectedTab;
    return Tab.Info;
  };

  return (
    <div>
      <OrigamiPreview
        spell={spellName}
        publisherCountry={wizard.publisher?.country}
        origamiData={origamiData}
        selectedTab={defineTab()}
        showExpanded={showExpanded}
      />
      <div className="question">
        <h3 dangerouslySetInnerHTML={renderQuestion()} />
        {description && <h4>{description}</h4>}
        {prependValue && (
          <div className="input-prepend" data-label={prependValue}>
            {renderInput(textArea)}
          </div>
        )}
        {!prependValue && renderInput(textArea)}

        <ButtonOptions
          changePath={wizard.changePath}
          previous={previous}
          handleSubmit={handleSubmit}
        />
      </div>
    </div>
  );
};

export default FillOrigamiStep;
